import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { Button, ButtonDirective } from 'primeng/button'
import { MultiSelect } from 'primeng/multiselect'
import { Dialog } from 'primeng/dialog'
import { DatePicker } from 'primeng/datepicker'
import { Select } from 'primeng/select'
import { Textarea } from 'primeng/textarea'
import { InputText } from 'primeng/inputtext'
import { UserService } from '../../../users/services/user.service'
import { ToastService } from '../../../../shared/services/toast.service'
import { UserModel } from '../../../../core/models/user.model'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { NgClass, NgForOf, NgIf } from '@angular/common'
import { EventsService } from '../../services/events.service'
import { Checkbox } from 'primeng/checkbox'
import { DropdownModule } from 'primeng/dropdown'
import { InputNumber } from 'primeng/inputnumber'
import { RadioButton } from 'primeng/radiobutton'
import { ToggleButton } from 'primeng/togglebutton'
import { TabView } from 'primeng/tabview'
import { TabPanel } from 'primeng/tabview'

@Component({
  selector: 'app-edit-modal',
  standalone: true,
  imports: [
    MultiSelect,
    Dialog,
    DatePicker,
    Select,
    Textarea,
    InputText,
    ButtonDirective,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
    DropdownModule,
    InputNumber,
    NgForOf,
    Checkbox,
    ToggleButton,
    RadioButton,
    TabView,
    TabPanel,
    NgClass,
  ],
  templateUrl: './edit-modal.component.html',
  styleUrl: './edit-modal.component.scss',
})
export class EditModalComponent implements OnInit {
  @Input() isVisible: boolean = false
  @Input() selectedEvent: any = null
  @Output() isVisibleChange = new EventEmitter<boolean>()
  @Output() evenementModifie = new EventEmitter<void>()

  public allTechnicians: UserModel[] = []
  public allEventTypes: any = []
  public erpSource: string = ''
  public form: FormGroup
  private userModifiedName: boolean = false
  public submitting: boolean = false

  // Variables pour gérer le dialogue de scope de récurrence
  public showRecurrenceEditDialog: boolean = false
  public selectedEditScope: 'this' | 'all' | 'thisAndFuture' | null = null

  // Variable pour savoir si l'événement est récurrent
  public isRecurrentEvent: boolean = false

  // Propriétés de récurrence de l'événement
  public weekDays = [
    { label: 'Lun', value: 1, selected: false },
    { label: 'Mar', value: 2, selected: false },
    { label: 'Mer', value: 3, selected: false },
    { label: 'Jeu', value: 4, selected: false },
    { label: 'Ven', value: 5, selected: false },
    { label: 'Sam', value: 6, selected: false },
    { label: 'Dim', value: 0, selected: false },
  ]

  public selectedDays: number[] = []

  public recurrenceUnits = [
    { label: 'jour', value: 'day' },
    { label: 'semaine', value: 'week' },
    { label: 'mois', value: 'month' },
    { label: 'année', value: 'year' },
  ]

  constructor(
    private userServices: UserService,
    private toastService: ToastService,
    private eventsService: EventsService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.loadAllTechnicians()
    this.initForm()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      this.open()
    }
  }

  hide() {
    this.isVisible = false
    this.isVisibleChange.emit(false)
    this.selectedEditScope = null
    this.showRecurrenceEditDialog = false
  }

  open() {
    if (!this.selectedEvent) return

    this.userModifiedName = false
    const eventData = this.selectedEvent.extendedProps?.data

    // Vérifier si l'événement est récurrent
    this.isRecurrentEvent = !!eventData?.recurrenceRule

    // Récupérer l'erpSource de l'événement
    this.erpSource = eventData?.erpSource || ''

    // Préparer les dates
    let eventStart: Date
    if (this.selectedEvent.start instanceof Date) {
      eventStart = new Date(this.selectedEvent.start.getTime())
    } else {
      eventStart = new Date(this.selectedEvent.start)
    }

    let eventEnd: Date
    if (this.selectedEvent.end) {
      if (this.selectedEvent.end instanceof Date) {
        eventEnd = new Date(this.selectedEvent.end.getTime())
      } else {
        eventEnd = new Date(this.selectedEvent.end)
      }
    } else {
      eventEnd = new Date(eventStart.getTime() + 30 * 60000)
    }

    // Calculer la durée
    const durationMs = eventEnd.getTime() - eventStart.getTime()
    const durationDate = new Date(0, 0, 0)
    durationDate.setHours(Math.floor(durationMs / (1000 * 60 * 60)))
    durationDate.setMinutes(
      Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))
    )

    // Récupérer les utilisateurs - ils sont directement dans eventData
    // usersIds contient les IDs ERP des utilisateurs
    const eventUsers = eventData?.usersIds || []
    const erpSource = eventData?.erpSource || ''

    // Mapper les utilisateurs de l'événement avec les technicians complets
    // Les usersIds correspondent aux id_{ErpSource} des techniciens
    // Par exemple: id_Nrj, id_Ml, etc. (première lettre en majuscule)
    const erpSourceCapitalized =
      erpSource.charAt(0).toUpperCase() + erpSource.slice(1)
    const erpIdField = `id${erpSourceCapitalized}` as keyof UserModel
    const selectedUsers = this.allTechnicians.filter((tech) =>
      eventUsers.includes(tech[erpIdField])
    )

    // Parser la règle de récurrence si elle existe
    if (this.isRecurrentEvent && eventData.recurrenceRule) {
      this.parseRecurrenceRule(eventData.recurrenceRule)
    }

    // Si l'erpSource existe, charger les types d'événements puis patcher le formulaire
    if (this.erpSource !== '') {
      this.loadAllEventTypes(this.erpSource).subscribe({
        next: () => {
          // Trouver le type d'événement maintenant que les types sont chargés
          // Le type d'événement est dans eventData.eventType.id
          const eventType = this.allEventTypes.find(
            (type: any) => type.id === eventData?.eventType?.id
          )

          // Patcher le formulaire avec les données de l'événement
          this.form.patchValue({
            users: selectedUsers,
            eventType: eventType || null,
            name: eventData?.name || '',
            description: eventData?.description || '',
            startTime: eventStart,
            duration: durationDate,
            address: eventData?.address || '',
            postalCode: eventData?.postalCode || '',
            city: eventData?.locality || '',
            recurrenceEnabled: this.isRecurrentEvent,
          })
        },
        error: () => {
          // En cas d'erreur, patcher quand même le formulaire sans le type d'événement
          this.form.patchValue({
            users: selectedUsers,
            eventType: null,
            name: eventData?.name || '',
            description: eventData?.description || '',
            startTime: eventStart,
            duration: durationDate,
            address: eventData?.address || '',
            postalCode: eventData?.postalCode || '',
            city: eventData?.locality || '',
            recurrenceEnabled: this.isRecurrentEvent,
          })
        },
      })
    } else {
      // Pas d'erpSource, patcher le formulaire sans le type d'événement
      this.form.patchValue({
        users: selectedUsers,
        eventType: null,
        name: eventData?.name || '',
        description: eventData?.description || '',
        startTime: eventStart,
        duration: durationDate,
        address: eventData?.address || '',
        postalCode: eventData?.postalCode || '',
        city: eventData?.locality || '',
        recurrenceEnabled: this.isRecurrentEvent,
      })
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      users: [[], Validators.required],
      eventType: [null, Validators.required],
      name: ['', Validators.required],
      description: [''],
      startTime: [null, Validators.required],
      duration: [null, Validators.required],
      address: [''],
      postalCode: [''],
      city: [''],
      recurrenceEnabled: [false],
      recurrenceInterval: [1],
      recurrenceUnit: ['day'],
      recurrenceEndType: ['never'],
      recurrenceEndAfter: [null],
      recurrenceEndDate: [null],
    })

    // Observer les changements du type d'événement
    const eventTypeControl = this.form.get('eventType')
    if (eventTypeControl) {
      eventTypeControl.valueChanges.subscribe((selectedEventType) => {
        const nameControl = this.form.get('name')
        if (
          selectedEventType &&
          nameControl &&
          (!nameControl.value || !this.userModifiedName)
        ) {
          nameControl?.setValue(selectedEventType.name)
        }
      })
    }

    // Observer les changements du nom
    const nameControl = this.form.get('name')
    if (nameControl) {
      nameControl.valueChanges.subscribe((value) => {
        if (value && value.trim() !== '') {
          this.userModifiedName = true
        }
      })
    }
  }

  loadAllTechnicians(): void {
    this.userServices.getUsersWithFilters({}).subscribe({
      next: (technicians) => {
        this.allTechnicians = technicians.map((tech) => ({
          ...tech,
          fullName: `${tech.firstname} ${tech.lastname}`,
        }))
      },
      error: (error) => {
        this.toastService.showError(error.message)
      },
    })
  }

  loadAllEventTypes(erpSource: string) {
    const observable = this.eventsService.getEventsTypes(erpSource)
    observable.subscribe({
      next: (data) => {
        this.allEventTypes = data
      },
      error: (error) => {
        this.toastService.showError(error.message)
      },
    })
    return observable
  }

  parseRecurrenceRule(rule: any): void {
    // Parser la règle de récurrence
    // Le backend envoie un objet avec: frequency, interval, count, until, daysOfWeek, etc.
    if (!rule) return

    // Si c'est un objet (format du backend)
    if (typeof rule === 'object') {
      // Mapper la fréquence
      if (rule.frequency) {
        this.form.patchValue({ recurrenceUnit: rule.frequency })
      }

      // Interval
      if (rule.interval) {
        this.form.patchValue({ recurrenceInterval: rule.interval })
      }

      // Jours de la semaine
      if (
        rule.daysOfWeek &&
        Array.isArray(rule.daysOfWeek) &&
        rule.daysOfWeek.length > 0
      ) {
        this.selectedDays = rule.daysOfWeek
        this.weekDays.forEach((wd) => {
          wd.selected = rule.daysOfWeek.includes(wd.value)
        })
      }

      // Type de fin de récurrence
      if (rule.count) {
        this.form.patchValue({
          recurrenceEndType: 'after',
          recurrenceEndAfter: rule.count,
        })
      } else if (rule.until) {
        this.form.patchValue({
          recurrenceEndType: 'until',
          recurrenceEndDate: new Date(rule.until),
        })
      } else {
        this.form.patchValue({
          recurrenceEndType: 'never',
        })
      }
    }
    // Si c'est une chaîne (format RRULE)
    else if (typeof rule === 'string') {
      const parts = rule.split(';')
      parts.forEach((part) => {
        const [key, value] = part.split('=')

        switch (key) {
          case 'FREQ':
            const freqMap: any = {
              DAILY: 'day',
              WEEKLY: 'week',
              MONTHLY: 'month',
              YEARLY: 'year',
            }
            this.form.patchValue({ recurrenceUnit: freqMap[value] || 'day' })
            break

          case 'INTERVAL':
            this.form.patchValue({ recurrenceInterval: parseInt(value) })
            break

          case 'BYDAY':
            const dayMap: any = {
              MO: 1,
              TU: 2,
              WE: 3,
              TH: 4,
              FR: 5,
              SA: 6,
              SU: 0,
            }
            const days = value
              .split(',')
              .map((d) => dayMap[d])
              .filter((d) => d !== undefined)
            this.selectedDays = days
            this.weekDays.forEach((wd) => {
              wd.selected = days.includes(wd.value)
            })
            break

          case 'COUNT':
            this.form.patchValue({
              recurrenceEndType: 'after',
              recurrenceEndAfter: parseInt(value),
            })
            break

          case 'UNTIL':
            this.form.patchValue({
              recurrenceEndType: 'until',
              recurrenceEndDate: new Date(value),
            })
            break
        }
      })
    } else {
      console.warn('Recurrence rule format not recognized:', rule)
    }
  }

  updateEvenement() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      this.toastService.showError(
        "Formulaire de modification d'événement incomplet"
      )
      return
    }

    // Si l'événement est récurrent, demander le scope
    if (this.isRecurrentEvent) {
      this.showRecurrenceEditDialog = true
    } else {
      // Sinon, procéder directement à la mise à jour
      this.performUpdate('this')
    }
  }

  /**
   * Sélectionne le scope de modification et ferme le dialogue
   */
  selectEditScope(scope: 'this' | 'all' | 'thisAndFuture'): void {
    this.selectedEditScope = scope
    this.showRecurrenceEditDialog = false
    this.performUpdate(scope)
  }

  /**
   * Ferme le dialogue de scope de modification
   */
  closeRecurrenceEditDialog(): void {
    this.showRecurrenceEditDialog = false
    this.selectedEditScope = null
  }

  /**
   * Effectue la mise à jour de l'événement
   */
  performUpdate(editScope: 'this' | 'all' | 'thisAndFuture'): void {
    const value = this.form.value
    const eventData = this.selectedEvent.extendedProps?.data
    const eventId = eventData?.id
    const parentEventId = eventData?.parentEventId

    if (!eventId) {
      this.toastService.showError('Impossible de modifier cet événement')
      return
    }

    // Activer l'indicateur de chargement
    this.submitting = true

    const payload: any = {
      eventTypeId: value.eventType?.id,
      name: value.name,
      description: value.description || '',
      startDate: this.formatDateToISO(value.startTime),
      duration: this.formatDuration(value.duration),
      address: value.address || '',
      postalCode: value.postalCode || '',
      locality: value.locality || '',
      isAllDay: false,
      usersIds: value.users.map((u: UserModel) => u.id),
      erpSource: this.erpSource,
    }

    // Ajouter le scope de modification pour les événements récurrents
    if (this.isRecurrentEvent) {
      payload.update_scope = editScope

      // Si on modifie "cette occurrence et les suivantes", ajouter la date de l'événement
      if (editScope === 'thisAndFuture') {
        payload.eventDate = this.formatDateToISO(this.selectedEvent.start)
      }

      // Ajouter les informations de récurrence si modifiées
      if (value.recurrenceEnabled) {
        // Mapper les unités de récurrence vers le format attendu par le backend
        const frequencyMap: any = {
          day: 'daily',
          week: 'weekly',
          month: 'monthly',
          year: 'yearly',
        }

        payload.recurrence = {
          frequency: frequencyMap[value.recurrenceUnit] || value.recurrenceUnit,
          interval: value.recurrenceInterval || 1,
          daysOfWeek: this.selectedDays || [],
          count: value.recurrenceEndAfter || null,
          until: value.recurrenceEndDate || null,
        }
      }
    }

    // Appeler le service approprié
    const updateObservable = this.isRecurrentEvent
      ? this.eventsService.updateWithRecurrence(
          parentEventId || eventId,
          payload
        )
      : this.eventsService.update(eventId, payload)

    updateObservable.subscribe({
      next: () => {
        let successMessage = 'Événement modifié avec succès'
        if (this.isRecurrentEvent) {
          switch (editScope) {
            case 'this':
              successMessage = 'Occurrence modifiée avec succès'
              break
            case 'all':
              successMessage = 'Toutes les occurrences modifiées avec succès'
              break
            case 'thisAndFuture':
              successMessage =
                'Cette occurrence et les futures modifiées avec succès'
              break
          }
        }
        this.evenementModifie.emit()
        this.toastService.showSuccess(successMessage)
        this.submitting = false
        this.hide()
      },
      error: (error) => {
        this.submitting = false
        this.toastService.showError(
          error.message || 'Erreur lors de la modification'
        )
      },
    })
  }

  formatDuration(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  formatDateToISO(date: Date): string {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const seconds = date.getSeconds().toString().padStart(2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  }

  setEventTypeName($event: any) {
    const name = this.form.get('name')
    if (name) {
      name.setValue($event.value?.name || '')
    }
  }

  toggleDay(day: number) {
    const dayObj = this.weekDays.find((d) => d.value === day)
    if (dayObj) {
      dayObj.selected = !dayObj.selected
      if (dayObj.selected) {
        if (!this.selectedDays.includes(day)) {
          this.selectedDays.push(day)
        }
      } else {
        this.selectedDays = this.selectedDays.filter((d) => d !== day)
      }
    }
  }

  isDaySelected(day: number): boolean {
    return this.selectedDays.includes(day)
  }
}
